<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />

          <Button
            v-if="getUserPermission('CREATE_OVERDRAFT')"
            :label="$t('Add')"
            size="md"
            @click="goToAddOverdraft"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="lienRef"
      :busy="isBusy"
      :items="overdraftList"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission('MODIFY_OVERDRAFT') && (data.item.status.toLowerCase() !== 'expired')"
              @click="goToEditOverdraft(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">{{ $t('Modify') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="goToEditOverdraft(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission(data.item.status === 'Active' ? 'DEACTIVATE_OVERDRAFT' : 'ACTIVATE_OVERDRAFT')"
              @click="overdraftStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ getStatus(data.item.status ) }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="buttonLabel"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      :ok-disabled="isLoading"
      centered
      size="lg"
      :busy="isBusy"
      :title="modalTitle"
      @hidden="resetModal"
    >
      <validation-observer ref="overdraftRules">
        <b-form>
          <app-collapse
            class="business-items mb-4"
            type="margin"
            accordion
          >
            <app-collapse-item
              :title="$t('Account Information')"
              class="card-item"
              is-visible
            >
              <b-row class="match-height">
                <!-- Account Name -->
                <b-col md="6">
                  <InputField
                    id="account-name"
                    v-model="accountInfo.accountName"
                    :label="$t('Account Name')"
                    type="text"
                    for="account-name"
                    readonly
                  />
                </b-col>
                <!-- Account Name -->

                <!-- Product Name -->
                <b-col md="6">
                  <InputField
                    id="product-name"
                    v-model="accountInfo.productName"
                    :label="$t('Product Name')"
                    type="text"
                    for="product-name"
                    readonly
                  />
                </b-col>
                <!-- Product Name -->

                <!-- Account Number -->
                <b-col md="6">
                  <InputField
                    id="account-number"
                    v-model="overdraftFormData.accountNumber"
                    :label="$t('Account Number')"
                    type="number"
                    for="account-number"
                    readonly
                  />
                </b-col>
                <!--  Account Number -->

                <!-- Ledger Balance -->
                <b-col md="6">
                  <InputField
                    id="ledger-balance"
                    v-model="accountInfo.ledgerBalance"
                    :label="$t('Ledger Balance')"
                    type="text"
                    for="ledger-balance"
                    readonly
                  />
                </b-col>
                <!-- Ledger Balance -->

                <!-- Available Balance -->
                <b-col md="6">
                  <InputField
                    id="available-balance"
                    v-model="accountInfo.availableBalance"
                    :label="$t('Available Balance')"
                    type="text"
                    for="available-balance"
                    readonly
                  />
                </b-col>
                <!-- Available Balance -->

                <!-- Withdrawal Balance -->
                <b-col md="6">
                  <InputField
                    id="overdraft-limit"
                    v-model="accountInfo.withdrawableBalance"
                    :label="$t('Withdrawal Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <!-- Withdrawal Balance -->
              </b-row>
            </app-collapse-item>
          </app-collapse>
          <h5 class="text-capitalize mb-2">
            {{ $t('Overdraft Details') }}
          </h5>
          <b-row class="match-height">
            <!--Overdraft Limit-->
            <b-col md="6">
              <InputNumberField
                id="overdraft-limit"
                v-model.number="overdraftFormData.limit"
                :label="$t('Overdraft Limit')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Overdraft Limit -->

            <!-- Tenure -->
            <b-col md="6">
              <InputNumberField
                id="tenure"
                v-model="overdraftFormData.tenure"
                :label="$t('Tenure (Days)')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Tenure -->

            <!-- Start Date-->
            <b-col md="6">
              <InputField
                id="start-date"
                v-model="overdraftFormData.commencementDate"
                :label="$t('Start Date')"
                type="text"
                readonly
              />
            </b-col>
            <!--  Start Date -->

            <!-- End Date-->
            <b-col md="6">
              <InputField
                id="end-date"
                v-model="endDate"
                :label="$t('End Date')"
                type="text"
                readonly
              />
            </b-col>
            <!--  End Date -->

            <!-- Fee -->
            <b-col md="6">
              <SelectField
                id="fee"
                v-model="feeId"
                :reduce="option => option.value"
                :label="$t('Fee')"
                :options="feeOptions"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Fee -->

            <b-col md="6">
              <!-- Fee Amount -->
              <InputField
                id="fee-amount"
                v-model.number="overdraftFormData.feeAmount"
                :label="$t('Fee Amount')"
                type="number"
                readonly
              />
              <!-- Fee Amount -->
            </b-col>

            <b-col md="6">
              <!-- Interest Rate-->
              <SelectField
                id="interest"
                v-model="interestId"
                :label="$t('Interest Rate')"
                :reduce="option => option.value"
                :options="interestOptions"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>

            <b-col md="6">
              <!-- Interest Rate -->
              <InputField
                id="interest-rate"
                v-model.number="overdraftFormData.interestAmount"
                :label="$t('Interest Rate')"
                type="number"
                readonly
              />
            </b-col>
          </b-row>

          <h5 class="text-capitalize mb-3 mt-3">
            {{ $t('Security Pledge') }}
          </h5>
          <b-row>
            <b-col md="6">
              <SwitchButton
                v-model="overdraftFormData.securityPledged"
                :label="$t('Any Security Pledge?')"
                name="security-pledge"
                :readonly="isReadonly"
              />
            </b-col>
          </b-row>

          <b-row v-if="overdraftFormData.securityPledged">
            <!-- Type of security -->
            <b-col md="6">
              <SelectField
                id="interest"
                v-model="overdraftFormData.securityType"
                :label="$t('Type of security')"
                :reduce="option => option.value"
                :options="securityTypesOptions"
                item="label"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Type of security -->

            <!-- Security valuation  -->
            <b-col md="6">
              <InputNumberField
                id="security-valuation"
                v-model.number="overdraftFormData.securityValuation"
                :label="$t('Security Valuation')"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Security valuation -->

            <!-- Detailed Description -->
            <b-col md="12">
              <TextAreaField
                id="detailed-description"
                v-model="overdraftFormData.description"
                :label="$t('Detailed Description')"
                rows="5"
                :readonly="isReadonly"
              />
            </b-col>
            <!-- Detailed Description -->
          </b-row>
        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      no-close-on-backdrop
      centered
      size="lg"
      title="Filters"
      modal-footer
      @ok="fetchOverdraft"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!--Account Name -->
        <b-col md="6">
          <InputField
            id="name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="number"
          />
        </b-col>
        <!-- account Number -->

        <!-- Product -->
        <b-col md="6">
          <SelectField
            id="product"
            v-model="filter.productCode"
            :label="$t('Product')"
            :reduce="option => option.value"
            :options="productOptions"
            item="label"
            :placeholder="$t('Select product')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Product -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.overdraftStatus"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="overdraftStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BBadge,
  BModal, BForm, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required, maxValue, positive } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapActions } from 'vuex'
import { permissionMixin } from '@core/mixins/permission'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toAmount, toBalance } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import { gender, accessLevel, securityTypes } from '../../../../../helpers/data'
import { toYearWithDate, toDateWithYear } from '../../../../../helpers/formatDate'
import getStatusColor from '../../../../../helpers/getStatusColor'
import getEndDate from '../../../../../helpers/getEndDate'
import addSpaceToString from '../../../../../helpers/addSpaceToString'
import Button from '../../../../components/button/Button.vue'
import SwitchButton from '../../../../components/switch/Switch.vue'
import TextAreaField from '../../../../forms/TextAreaField.vue'
import toNumber from '../../../../../helpers/toNumber'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import { accountPermissions } from '../../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BTable,
    BDropdown,
    BDropdownItem,
    BModal,
    BForm,
    BSpinner,
    SelectField,
    ValidationObserver,
    InputField,
    TablePagination,
    FullScreenSpinner,
    Button,
    TableEntries,
    SwitchButton,
    TextAreaField,
    InputNumberField,
    AppCollapse,
    AppCollapseItem,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      showSpinner: false,
      isReadonly: false,
      isLoading: false,
      isDisabled: false,
      maxValue,
      toAmount,
      toNumber,
      addSpaceToString,
      positive,
      overdraftFormData: {
        accountNumber: '',
        limit: 0,
        tenure: 0,
        commencementDate: null,
        useDefaultFee: true,
        feeCode: '',
        feeAmount: 0,
        useDefaultInterest: true,
        interestCode: '',
        interestAmount: 0,
        securityPledged: false,
        securityType: '',
        securityValuation: 0,
        description: '',
        initiator: '',
        guarantorName: '',
        guarantorPhoneNumber: '',
      },
      required,
      modalTitle: '',
      interestOptions: [],
      feeOptions: [],
      getStatusColor,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'productName', sortable: true, label: 'product' },
        { key: 'limit', sortable: true, label: 'OD Limit' },
        { key: 'tenure', sortable: true, label: 'Tenure' },
        { key: 'commencementDate', sortable: true, label: 'Start Date' },
        { key: 'maturityDate', sortable: true, label: 'Maturity Date' },
        { key: 'interestAmount', sortable: true, label: 'Interest Rate' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      toDateWithYear,
      toYearWithDate,
      maturityDate: '',
      genderOptions: gender,
      buttonLabel: 'Save',
      isFlatAmountRequired: '',
      isPercentageRequired: '',
      isFlatAmount: false,
      isPercentage: false,
      datePickerConfig: {
        dateFormat: 'd/m/Y',
        ariaDateFormat: 'd/m/Y',
      },
      setMaturityDate(dateStr) {
        this.maturityDate = getEndDate(dateStr, this.lienFormData.tenure)
      },
      filter: {
        accountName: '',
        product: '',
        accountNumber: '',
        overdraftStatus: '',
        sortColumn: '',
        direction: '',
      },
      accountInfo: {
        accountName: '',
        productName: '',
        accountNumber: '',
        ledgerBalance: '',
        availableBalance: '',
        withdrawalBalance: '',
      },
      overdraftStatusOptions: [
        { label: this.$t('Active'), value: 'Active' },
        { label: this.$t('Disabled'), value: 'Disabled' },
        { label: this.$t('Expired'), value: 'Expired' },
        { label: this.$t('Inactive'), value: 'Inactive' },
        { label: this.$t('Pending'), value: 'Pending' },
      ],
      accessLevelOptions: accessLevel,
      overdraftList: [],
      endDate: '',
      feeId: '',
      interestId: '',
      securityTypesOptions: securityTypes,
      feeDetails: [],
      interestDetails: [],
      productOptions: [],
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchOverdraft()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchOverdraft()
    },
  },
  mounted() {
    this.fetchOverdraft()
    this.fetchInterest()
    this.fetchFees()
    this.fetchCASAProducts()
  },

  methods: {
    ...mapActions({
      getOverdrafts: 'account/getOverdraftList',
      updateOverdraft: 'account/updateOverdraftAction',
      getInterests: 'account/getInterestList',
      getFees: 'account/getCAMFeeList',
      updateOverdraftStatus: 'account/updateStatus',
      searchCustomerAccount: 'account/searchCustomerAccountAction',
      getCASAProduct: 'account/getProducts',
    }),
    goToAddOverdraft() {
      this.$router.push({
        name: 'overdraft',
        params: {
          type: 'add',
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    goToEditOverdraft(item, activity, val) {
      this.$router.push({
        name: 'overdraft',
        params: {
          type: val,
          id: item.id,
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    getStatus(item) {
      if (item === 'Disabled' || item === 'Inactive') {
        return 'Activate'
      }
      return 'Deactivate'
    },
    async overdraftStatus(items) {
      const { id, accountName } = items
      const actionType = items.status === 'Active' ? 'Disable' : 'Active'
      const action = items.status === 'Active' ? 'Deactivate' : 'Activate'
      const permission = items.status === 'Active' ? accountPermissions.DEACTIVATE_OVERDRAFT : accountPermissions.ACTIVATE_OVERDRAFT

      const updateStatus = async () => {
        try {
          this.showSpinner = true
          const statusValues = {
            id,
            name: accountName,
          }
          const response = await this.updateOverdraftStatus({
            statusValues, actionType, routeName: 'AccountOverdraft', permission,
          })
          if (response.status === 200) {
            this.fetchOverdraft()
            const requestText = this.$t('Your request to')
            const successText = this.$t('overdraft was successful')
            this.alertSuccess({ text: `${requestText} ${action} ${accountName} ${successText}` })
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      const text1 = this.$t('Are you sure you want to')
      const text2 = this.$t('overdraft for')
      this.confirm(updateStatus, { title: '', text: `${text1} ${action} ${text2} ${accountName}?` })
    },
    async fetchOverdraft() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getOverdrafts({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.overdraftList = data.data.map(item => ({
            ...item,
            limit: toBalance(item.limit),
            commencementDate: item.commencementDate ? toDateWithYear(item.commencementDate) : null,
            maturityDate: item.maturityDate ? toDateWithYear(item.maturityDate) : null,
          }))
          this.totalCount = data.recordCount
          this.isBusy = false
        } else {
          this.overdraftList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async onSelectOverdraft(items, activity, val) {
      const fee = this.feeDetails.find(item => item.code === items.feeCode)
      const interest = this.interestDetails.find(item => item.code === items.interestCode)
      this.overdraftFormData = {
        ...items,
        commencementDate: toDateWithYear(items.commencementDate),
        feeAmount: items.feeAmount === 0 ? items.feeAmount.toString() : items.feeAmount,
        interestAmount: items.interestAmount === 0 ? items.interestAmount.toString() : items.interestAmount,
      }
      this.feeId = fee.name
      this.interestId = interest.name
      this.endDate = toDateWithYear(items.maturityDate)
      const customerDetails = await this.searchCustomerAccount({
        search: { accountNumber: items.accountNumber },
        page: this.currentPage,
        limit: this.perPage,
      })
      const customerData = await customerDetails.data.data[0]
      this.accountInfo = {
        ...customerData,
        ledgerBalance: toBalance(customerData.ledgerBalance),
        availableBalance: toBalance(customerData.availableBalance),
        withdrawableBalance: toBalance(customerData.withdrawableBalance),
      }
      this.isReadonly = val !== 'edit'
      this.modalTitle = val === 'edit' ? this.$t('Edit Overdraft') : this.$t('Overdraft')
      this.buttonLabel = this.$t('Update')
      this.$root.$emit('bv::show::modal', 'modal-lg')
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getCASAProduct({
          search: { type: 'Savings' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_OVERDRAFT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data.map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchInterest() {
      try {
        const response = await this.getInterests({
          search: '',
          page: 0,
          limit: 0,
        })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.interestDetails = data.data
          this.interestOptions = data.data.filter(dataItem => dataItem.status === 'Active' && dataItem.type === 'Debit').map(item => ({ label: item.name, value: item.id }))
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },
    async fetchFees() {
      try {
        const response = await this.getFees({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_OVERDRAFT,
        })
        const { status, data } = await response

        if (status === 200 && data.data) {
          this.feeDetails = data.data
          this.feeOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.id }))
        }
      } catch (error) {
        this.toastAlert({ title: `${error}` })
      }
    },

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchOverdraft()
    },
    resetModal() {
      this.isPercentage = false
      this.isLoading = false
      this.isFlatAmount = false
      this.isReadonly = false
      this.modalTitle = this.$t('Lien')
      this.buttonLabel = this.$t('Update')
    },
  },
}
</script>
